import { Entity } from '@backstage/catalog-model';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import { useMemo } from 'react';
import useAsync from 'react-use/lib/useAsync';

import {
  DetectedFramework,
  FrameworkId,
} from '@internal/backstage-plugin-framework-versions-common';

import { eolApiRef } from '../apis/EolApi';
import { readFrameworks } from '../lib/frameworks';
import { FrameworkStatus, Release, evaluateStatus } from '../lib/status';

export type FrameworkRow = {
  id: FrameworkId;
  version: string;
  status: FrameworkStatus;
  eol?: string;
};

export type EolData = {
  releases?: Partial<Record<FrameworkId, Release[]>>;
  minimums: Record<string, string>;
};

export const evaluateFrameworks = (
  detected: DetectedFramework[],
  { releases, minimums }: EolData,
  now = new Date(),
): FrameworkRow[] =>
  detected.map(({ id, version }) => ({
    id,
    version,
    ...evaluateStatus({
      version,
      releases: releases?.[id],
      minimum: minimums[id],
      now,
    }),
  }));

// `ids` loads end-of-life data for more frameworks than the entity uses.
export const useFrameworkStatuses = (
  entity: Entity,
  ids?: readonly FrameworkId[],
) => {
  const eolApi = useApi(eolApiRef);
  const config = useApi(configApiRef);
  const detected = useMemo(() => readFrameworks(entity), [entity]);
  const entityIds = useMemo(
    () => [...new Set(detected.map(d => d.id))],
    [detected],
  );
  const toLoad = ids ?? entityIds;
  const minimums = useMemo(
    () =>
      config
        .getOptionalConfig('frameworkVersions.minimums')
        ?.get<Record<string, string>>() ?? {},
    [config],
  );

  const { loading, value } = useAsync(async () => {
    const results = await Promise.allSettled(
      toLoad.map(id => eolApi.getReleases(id)),
    );
    const releases: Partial<Record<FrameworkId, Release[]>> = {};
    results.forEach((result, i) => {
      if (result.status === 'fulfilled') releases[toLoad[i]] = result.value;
    });
    return { releases, error: results.some(r => r.status === 'rejected') };
  }, [eolApi, toLoad]);

  const eolData = useMemo(
    () => ({ releases: value?.releases, minimums }),
    [value, minimums],
  );
  const rows = useMemo(
    () => evaluateFrameworks(detected, eolData),
    [detected, eolData],
  );

  return { rows, loading, error: value?.error ?? false, eolData };
};
